import type {
  IdentifiableDataItem,
  Links,
  Meta,
  Sort
} from '@library/types/utils';

/* --------------------- Basic enums and constant types --------------------- */

export const PROMOTION_TYPES = [
  'PayoutBoost',
  'ProtectedPlay',
  'FlashSale',
  'FreeEntryPromotion',
  // 'OfferCard',
  // 'StreaksRewardPromotion',
  'DepositMatch'
];

export type PromotionType = (typeof PROMOTION_TYPES)[number];
export type PromotionFundType = 'credit' | 'promo';
export type PromotionEntryType = 'credit' | 'promo';
export type PromotionPayoutType = 'credit' | 'promo';
export type PromotionApprovalsListType = 'pending' | 'approved' | 'expired';

/* ----------------------- Column types for tables ----------------------- */

export type PromotionsColumns =
  | 'id'
  | 'type'
  | 'name'
  | 'headline'
  | 'use_by'
  | 'created_by'
  | 'created_at'
  | 'macro_promo_tag'
  | 'micro_promo_tag'
  | 'approved_by'
  | 'approved_at';

export type SortedPromotionsColumn = Sort<PromotionsColumns>;

export type PromotionsPendingApprovalColumns =
  | 'id'
  | 'name'
  | 'memberCount'
  | 'payoutAmount'
  | 'createdAt'
  | 'availableAt';

export type ExpiredPromotionsColumns =
  | 'id'
  | 'name'
  | 'memberCount'
  | 'payoutAmount'
  | 'createdAt'
  | 'expiredAt';

export type ApprovedPromotionsColumns =
  | 'id'
  | 'name'
  | 'memberCount'
  | 'payoutAmount'
  | 'createdAt'
  | 'availableAt'
  | 'approvedAt'
  | 'approvedBy';

/* -------------------------- UI-related interfaces ------------------------- */

export interface PromotionPendingApprovalActionsVisibleMenuItems {
  approve: boolean;
  viewPromotion: boolean;
  viewPromoUsers: boolean;
}

/* ----------------------- Request/Response interfaces ---------------------- */

export type PromotionsQueryParams = {
  page?: number;
  perPage?: number;
  // sortBy: string;
  // sortDir: string;
};

export interface ApprovePromotionRequest {
  promotionId: number;
}

/** API response interfaces */

export interface PromotionAttributes {
  allowed_league_ids: number[];
  type: PromotionType;
  fund_type: PromotionFundType;
  payout_type: PromotionPayoutType;
  entry_type: PromotionEntryType;
  player_count: number;
  opt_in_by: string;
  use_by: string;
  short_description: string;
  long_description: string;
  payout_cents: number;
  max_cash_back_amount_cents: number;
  game_start_date: string;
  correlated_payouts_allowed: boolean;
  created_at: string;
  updated_at: string;
  payout_multipliers: string;
  name: string;
  opted_in_date: string;
  headline: string;
  entry_slip_description: string;
  opt_in: boolean;
  visible_to_users_at: string;
  discounted_line_score: string;
  projection_id: number;
  entry_amount_cents: number;
  custom_headshot: string;
  code: string;
  cta_link: string;
  cta_text: string;
  max_match_amount_cents: number;
  match_amount_percentage: number;
  all_live_squares: boolean;
  at_least_one_live_square: boolean;
  all_demons: boolean;
  at_least_one_demon: boolean;
  all_goblins: boolean;
  at_least_one_goblin: boolean;
  image: string;
  discounted_line_score_percentage: number;
  amount_remaining_cents: number;
  max_dollar_value: number;
  reimbursement_type: string;
}

export interface PromotionEligibleUserListData {
  id: string;
  type: string;
}

export interface PromotionRelationships {
  admin_user: IdentifiableDataItem;
  community_play: IdentifiableDataItem;
  micro_promo_tag: IdentifiableDataItem;
  macro_promo_tag: IdentifiableDataItem;
  projection: IdentifiableDataItem;
  promotion_eligible_user_list: IdentifiableDataItem;
  promotion_users: IdentifiableDataItem;
}

export type PromotionIncludedItemType =
  | 'admin_user'
  | 'community_play'
  | 'micro_promo_tag'
  | 'macro_promo_tag'
  | 'projection'
  | 'promotion_eligible_user_list'
  | 'promotion_users';

export type PromotionIncludedItem = {
  id: string;
  type: PromotionIncludedItemType;
  attributes: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    approved_at: string | null;
    user_count: number;
    name: string;
  };
  relationships: {
    approved_by: IdentifiableDataItem;
  };
};

export interface PromotionResponse {
  id: string;
  type: string;
  attributes: PromotionAttributes;
  relationships: PromotionRelationships;
}

export interface PromotionsResponse {
  data: Array<PromotionResponse>;
  included: Array<PromotionIncludedItem>;
  meta: Meta;
  links?: Links;
}

/* ------------- Formatted data interfaces (used in application) ------------ */

export interface FormattedPromotionAttributes {
  allowedLeagueIds: number[];
  type: PromotionType;
  fundType: PromotionFundType;
  payoutType: PromotionPayoutType;
  entryType: PromotionEntryType;
  playerCount: number;
  optInBy: string;
  useBy: string;
  shortDescription: string;
  longDescription: string;
  payoutCents: number;
  maxCashBackAmountCents: number;
  gameStartDate: string;
  correlatedPayoutsAllowed: boolean;
  createdAt: string;
  updatedAt: string;
  payoutMultipliers: string;
  name: string;
  optedInDate: string;
  headline: string;
  entrySlipDescription: string;
  optIn: boolean;
  availableAt: string;
  discountedLineScore: string;
  projectionId: number;
  entryAmountCents: number;
  customHeadshot: string;
  code: string;
  ctaLink: string;
  ctaText: string;
  maxMatchAmountCents: number;
  matchAmountPercentage: number;
  allLiveSquares: boolean;
  atLeastOneLiveSquare: boolean;
  allDemons: boolean;
  atLeastOneDemon: boolean;
  allGoblins: boolean;
  atLeastOneGoblin: boolean;
  image: string;
  discountedLineScorePercentage: number;
  amountRemainingCents: number;
  maxDollarValue: number;
  eligibleUserListCount: number;
  approvedAt?: string;
  approvedBy?: string;
  reimbursementType: string;
  createdBy: string;
  macroPromoTag: string;
  microPromoTag: string;
}

export interface FormattedPromotion {
  id: string;
  type: string;
  attributes: FormattedPromotionAttributes;
}

export interface FormattedPromotions {
  data: Array<FormattedPromotion>;
  meta: Meta;
}
export interface PromotionsRequest {
  page?: number;
  perPage?: number;
}
